/**
 * PdfPageViewer — full-screen, one-page-at-a-time viewer for a PDF of any length or weight.
 * Built for the Client Hub's PDF documents (docs/CLIENT_HUB_PDF_DOCUMENTS.md).
 *
 * Why not PdfFlipViewer: that one draws EVERY page before it shows any. A 17-page design deck
 * full of photographs measured ~5 s a page, so the client watched "Rendering…" for over a minute.
 * This one shows the page asked for as soon as it is drawn, then draws the next, the previous and
 * the rest in the background, one at a time, keeping each as a small JPEG.
 *
 * Props: { pdfUrl, images?, title?, downloadUrl?, onClose }. images = the page pictures saved at
 * upload ([{ url, w, h }], docs/CLIENT_HUB_PDF_DOCUMENTS.md): when given, the PDF is never opened
 * to view it, the pages are just those pictures (the next ones fetched ahead). Same chrome as
 * PdfFlipViewer (overlay, header, ‹ › buttons, arrow keys, Esc, swipe on phones).
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { loadPdfjs } from '../lib/pdfjs.js'

export default function PdfPageViewer({ pdfUrl, images, title, downloadUrl, onClose }) {
  const hasImages = Array.isArray(images) && images.length > 0
  const [total, setTotal] = useState(hasImages ? images.length : 0)
  const [cur, setCur] = useState(0)
  const [imgs, setImgs] = useState({})          // page index → object URL
  const [failed, setFailed] = useState(false)
  const curRef = useRef(0)
  const touchX = useRef(null)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 700

  useEffect(() => { curRef.current = cur }, [cur])

  // Saved page pictures: fetch the next two ahead so turning a page is instant.
  useEffect(() => {
    if (!hasImages) return
    ;[cur + 1, cur + 2].forEach(i => { if (images[i]) { const im = new Image(); im.src = images[i].url } })
  }, [hasImages, images, cur])

  // No pictures: load the document, then draw pages one at a time, the one being looked at first.
  useEffect(() => {
    if (hasImages) return
    let alive = true
    let pdf = null
    const made = []
    const done = new Set()
    ;(async () => {
      try {
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('no pdf.js')
        pdf = await pdfjs.getDocument({ url: pdfUrl }).promise
        if (!alive) return
        setTotal(pdf.numPages)
        const targetW = Math.min(2200, Math.round(window.innerWidth * Math.min(2, window.devicePixelRatio || 1)))
        while (alive && done.size < pdf.numPages) {
          const c = curRef.current
          const order = [c, c + 1, c - 1, c + 2]
          for (let i = 0; i < pdf.numPages; i++) order.push(i)
          const next = order.find(i => i >= 0 && i < pdf.numPages && !done.has(i))
          done.add(next)
          const page = await pdf.getPage(next + 1)
          const base = page.getViewport({ scale: 1 })
          const vp = page.getViewport({ scale: targetW / base.width })
          const canvas = document.createElement('canvas')
          canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
          const cctx = canvas.getContext('2d')
          cctx.fillStyle = '#fff'; cctx.fillRect(0, 0, canvas.width, canvas.height)
          await page.render({ canvasContext: cctx, viewport: vp }).promise
          const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.88))
          if (!alive || !blob) break
          const u = URL.createObjectURL(blob)
          made.push(u)
          setImgs(m => ({ ...m, [next]: u }))
        }
      } catch (e) {
        console.error('[PdfPageViewer]', e)
        if (alive) setFailed(true)
      }
    })()
    return () => {
      alive = false
      made.forEach(u => URL.revokeObjectURL(u))
      try { pdf && pdf.destroy() } catch { /* noop */ }
    }
  }, [pdfUrl, hasImages])

  const go = useCallback(d => setCur(c => Math.max(0, Math.min((total || 1) - 1, c + d))), [total])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' || e.key === 'PageDown') go(1)
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose])

  const src = hasImages ? (images[cur] && images[cur].url) : imgs[cur]
  const canBack = cur > 0
  const canNext = total > 0 && cur < total - 1

  return createPortal(
    <div style={St.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={St.header}>
        <div style={{ minWidth: 0 }}>
          <div style={St.hTitle}>{title || 'Document'}</div>
          {total > 0 && <div style={St.hMeta}>Page {cur + 1} of {total}</div>}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          <a href={downloadUrl || pdfUrl} target="_blank" rel="noopener noreferrer" style={St.hLink}>Download PDF ↓</a>
          <button onClick={onClose} style={St.hClose} title="Close (Esc)">✕</button>
        </div>
      </div>

      <div style={St.stage}
        onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
        onTouchStart={e => { touchX.current = e.touches[0].clientX }}
        onTouchEnd={e => {
          if (touchX.current == null) return
          const dx = e.changedTouches[0].clientX - touchX.current
          touchX.current = null
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1)
        }}>
        {!isMobile && <button onClick={() => go(-1)} disabled={!canBack} style={{ ...St.nav, opacity: canBack ? 1 : 0.25 }} title="Previous page">‹</button>}
        <div style={St.pageWrap}>
          {failed ? (
            <div style={St.msg}>This document could not be shown here. Use Download PDF to open it.</div>
          ) : src ? (
            <img src={src} alt={'Page ' + (cur + 1)} draggable={false} style={St.pageImg}
              onClick={e => { const r = e.currentTarget.getBoundingClientRect(); go(e.clientX - r.left > r.width / 2 ? 1 : -1) }} />
          ) : (
            <div style={St.msg}>{total ? 'Preparing page ' + (cur + 1) + '…' : 'Opening…'}</div>
          )}
        </div>
        {!isMobile && <button onClick={() => go(1)} disabled={!canNext} style={{ ...St.nav, opacity: canNext ? 1 : 0.25 }} title="Next page">›</button>}
        {isMobile && total > 1 && (
          <>
            <button onClick={() => go(-1)} disabled={!canBack} style={{ ...St.nav, ...St.navMobile, left: 14, opacity: canBack ? 1 : 0.3 }}>‹</button>
            <button onClick={() => go(1)} disabled={!canNext} style={{ ...St.nav, ...St.navMobile, right: 14, opacity: canNext ? 1 : 0.3 }}>›</button>
          </>
        )}
      </div>
      <div style={St.hint}>{isMobile ? 'Swipe or tap ‹ › to turn pages' : 'Click the page, use ‹ ›, or the arrow keys'}</div>
    </div>,
    document.body
  )
}

const St = {
  overlay:   { position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(7,39,45,0.96)', display: 'flex', flexDirection: 'column' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', color: '#fff', flexShrink: 0 },
  hTitle:    { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hMeta:     { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 },
  hLink:     { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', textDecoration: 'none', border: '0.5px solid rgba(255,255,255,0.4)', borderRadius: 6, padding: '6px 12px' },
  hClose:    { background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 },
  stage:     { position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, minHeight: 0, padding: '0 10px 10px' },
  pageWrap:  { flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pageImg:   { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', background: '#fff', boxShadow: '0 18px 50px rgba(0,0,0,0.35)', cursor: 'pointer' },
  nav:       { background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 30, lineHeight: 1, width: 44, height: 64, borderRadius: 8, cursor: 'pointer', flexShrink: 0 },
  navMobile: { position: 'absolute', bottom: 14, zIndex: 10, width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.16)' },
  msg:       { color: 'rgba(255,255,255,0.8)', fontFamily: '"DM Sans",sans-serif', fontSize: 14, textAlign: 'center', padding: 20 },
  hint:      { textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, padding: '0 0 14px' },
}
